import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useRestaurant } from "../../context/RestaurantContext";
import CartItem from "./CartItem";
import ConfirmModal from "../Modals/ConfirmModal";
import { isLocationServiceable } from "../../utils/locationUtils";
import { DUMMY_ADDONS } from "../../constants/offers";

const Cart = ({ onClose, location }) => {
  const { cart, getCartTotal, clearCart, addToCart } = useCart();
  const { isRestaurantOpen, formatTime, restaurantSettings } = useRestaurant();
  const navigate = useNavigate();
  const { subtotal } = getCartTotal();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const restaurantOpen = isRestaurantOpen();

  // Calculate Delivery Fee
  const calculateDeliveryFee = () => {
    if (!location?.lat || !location?.lng) return 0;

    const { isServiceable, distance } = isLocationServiceable(location.lat, location.lng);
    if (!isServiceable) return 0;

    const validDistance = distance || 0;

    const baseFee = 12;
    // Add 3 for every 500m (0.5km)
    const distanceCharges = Math.ceil(validDistance / 0.5) * 3;

    return baseFee + distanceCharges;
  };

  const deliveryFee = calculateDeliveryFee();
  const total = subtotal + deliveryFee;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-end bottom-75"
        style={{ zIndex: "999" }}
        onClick={onClose}
      >
        <div
          style={{ maxHeight: "75vh" }}
          className="bg-white w-full max-w-md mt-auto flex flex-col rounded-t-[30px] rounded-b-[30px] animate-slide-up overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-white border-b px-4 py-1 flex justify-between items-center z-10 rounded-t-[30px]">
            <h3 className="text-xl font-bold">Your Cart ({cart.length})</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
              aria-label="Close cart"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Restaurant Closed Banner */}
          {!restaurantOpen && (
            <div className="bg-red-50 border-b border-red-200 p-3">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">
                    Restaurant is currently closed
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    We'll be back at {formatTime(restaurantSettings.todayHours?.openingTime || restaurantSettings.openingTime)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {cart.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg
                className="w-20 h-20 mx-auto mb-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-lg font-semibold mb-2">Your cart is empty</p>
              <p className="text-sm mb-4">
                Add some delicious items to get started!
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg text-white font-semibold"
                style={{ backgroundColor: "#0B9444" }}
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto">
                {/* Cart Items */}
                <div className="ml-4 mr-4 mt-2 space-y-3 border rounded-lg">
                  {cart.map((item) => (
                    <CartItem key={item.cartId} item={item} />
                  ))}
                </div>

                {/* Add-ons Section */}
                <div className="add_ons px-4 py-2">
                  <h4 className="font-semibold mb-1 animate-flow">
                    Almost perfect… but perfection needs an add-on, doesn’t it?
                  </h4>
                  <div className="flex overflow-x-auto gap-4 pb-1 scrollbar-hide">
                    {DUMMY_ADDONS.map((addon) => (
                      <div
                        key={addon.id}
                        className="flex-shrink-0 w-32 border rounded-lg p-1 bg-white shadow-sm"
                      >
                        <div className="relative h-24 mb-1">
                          <img
                            src={addon.image}
                            alt={addon.name}
                            className="w-full h-full object-cover rounded-md"
                          />
                          <button
                            onClick={() => addToCart(addon)}
                            className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-md hover:bg-gray-50"
                            style={{ color: "#0B9444" }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="text-sm">
                          <p className="font-medium truncate">{addon.name}</p>
                          <p className="text-gray-500">₹{addon.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>


              </div>
              {/* Bill Summary */}
              <div className="border-t px-4 py-1 space-y-2 bg-gray-50">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Delivery Fee</span>
                  <span className="font-semibold">{deliveryFee === 0 ? 'Calculated at checkout' : `₹${deliveryFee}`}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-1 border-t">
                  <span>Total</span>
                  <span style={{ color: "#0B9444" }}>₹{total}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-4 py-1 space-y-2 border-t bg-white">
                <button
                  onClick={handleCheckout}
                  className="w-full py-1 rounded-lg text-white font-semibold text-lg hover:opacity-90 transition"
                  style={{ backgroundColor: "#0B9444" }}
                >
                  Proceed to Checkout
                </button>

                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="w-full py-1 text-sm text-red-600 hover:text-red-700 font-semibold"
                >
                  Clear Cart
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={clearCart}
        title="Clear Cart?"
        message="Are you sure you want to remove all items from your cart? This action cannot be undone."
        confirmText="Yes, Clear It"
        isDangerous={true}
      />
    </>
  );
};

export default Cart;
